/* The Cove Charleston: Dawn 15.5 enhancements */
(() => {
  'use strict';

  const scriptElement = document.currentScript;
  const configuredThreshold = Number(scriptElement?.dataset.coveShippingThreshold || 15000);
  const FREE_SHIPPING_THRESHOLD_CENTS = Number.isFinite(configuredThreshold) && configuredThreshold > 0
    ? configuredThreshold
    : 15000;

  const locale = document.documentElement.lang || 'en-US';
  const defaultCurrency = window.Shopify?.currency?.active || 'USD';
  let updateTimer;
  let requestInFlight = null;
  let dawnUnsubscribe;
  let fixedHeaderObserver;
  let menuFocusObserver;
  let cartNotificationObserver;
  let latestCartData = null;
  let menuFocusPointerHandlerBound = false;
  let cartNotificationBackdropHandlerBound = false;
  let loadAllProductsHandlerBound = false;
  let productBagStateHandlersBound = false;
  let emptyBagHandlersBound = false;
  let emptyBagReturnFocus = null;
  let emptyBagCloseTimer;
  let pageNavigationHandlersBound = false;
  let scrollBehaviorBeforeReset = null;
  let scrollResetTimers = [];

  const formatMoney = (cents, currency = defaultCurrency) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);

  const setHeaderState = () => {
    document
      .querySelectorAll('.header-wrapper')
      .forEach((header) => header.classList.toggle('cove-is-scrolled', window.scrollY > 12));
  };

  const initFixedHeader = () => {
    const shell = document.querySelector('[data-cove-fixed-header]');
    if (!shell) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        '--cove-fixed-header-height',
        `${Math.ceil(shell.getBoundingClientRect().height)}px`
      );
    };

    fixedHeaderObserver?.disconnect();
    syncHeight();

    if ('ResizeObserver' in window) {
      fixedHeaderObserver = new ResizeObserver(syncHeight);
      fixedHeaderObserver.observe(shell);
    } else {
      window.addEventListener('resize', syncHeight, { passive: true });
    }
  };

  const resetPageScrollPosition = (force = false) => {
    if (window.location.hash && !force) return;

    scrollResetTimers.forEach((timer) => window.clearTimeout(timer));
    scrollResetTimers = [];
    const root = document.documentElement;
    if (scrollBehaviorBeforeReset === null) {
      scrollBehaviorBeforeReset =
        typeof window.__covePreviousScrollBehavior === 'string'
          ? window.__covePreviousScrollBehavior
          : root.style.scrollBehavior;
    }
    root.style.scrollBehavior = 'auto';

    const applyTopPosition = () => {
      window.scrollTo(0, 0);
      root.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };

    applyTopPosition();
    window.requestAnimationFrame(() => {
      applyTopPosition();
      window.requestAnimationFrame(applyTopPosition);
    });

    [40, 120, 240].forEach((delay) => {
      scrollResetTimers.push(window.setTimeout(applyTopPosition, delay));
    });

    scrollResetTimers.push(
      window.setTimeout(() => {
        applyTopPosition();
        root.style.scrollBehavior = scrollBehaviorBeforeReset || '';
        scrollBehaviorBeforeReset = null;
        scrollResetTimers = [];
        window.__coveResetScrollRequested = false;
        window.__covePreviousScrollBehavior = undefined;
      }, 300)
    );
  };

  const requestNextPageAtTop = () => {
    window.__coveResetScrollRequested = true;

    try {
      window.sessionStorage.setItem('cove:reset-scroll', '1');
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (error) {
      // The immediate in-memory flag still covers browsers that restrict storage.
    }
  };

  const initPageNavigationScrollReset = () => {
    if (pageNavigationHandlersBound) return;

    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (error) {
      // The explicit reset below remains available when restoration cannot be configured.
    }

    document.addEventListener(
      'click',
      (event) => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest('a[href]');
        if (
          !link ||
          link.hasAttribute('download') ||
          link.hasAttribute('data-cove-preserve-scroll') ||
          (link.target && link.target !== '_self')
        ) {
          return;
        }

        let destination;
        try {
          destination = new URL(link.href, window.location.href);
        } catch (error) {
          return;
        }

        if (
          destination.origin !== window.location.origin ||
          !['http:', 'https:'].includes(destination.protocol)
        ) {
          return;
        }

        const isSameDocument =
          destination.pathname === window.location.pathname &&
          destination.search === window.location.search;

        if (isSameDocument && destination.hash) return;

        requestNextPageAtTop();

        if (isSameDocument) {
          resetPageScrollPosition(true);
        }
      },
      { capture: true }
    );

    window.addEventListener('popstate', () => {
      if (!window.location.hash) resetPageScrollPosition(true);
    });

    pageNavigationHandlersBound = true;
  };

  const getEmptyBagDialog = () => document.querySelector('[data-cove-empty-bag-dialog]');

  const closeEmptyBagDialog = () => {
    const dialog = getEmptyBagDialog();
    if (!dialog || dialog.hidden) return;

    window.clearTimeout(emptyBagCloseTimer);
    dialog.classList.remove('cove-empty-bag--active');
    dialog.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cove-empty-bag-active');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(emptyBagReturnFocus);
    } else {
      emptyBagReturnFocus?.focus();
    }

    emptyBagCloseTimer = window.setTimeout(() => {
      if (!dialog.classList.contains('cove-empty-bag--active')) dialog.hidden = true;
    }, 420);
  };

  const openEmptyBagDialog = (trigger) => {
    const dialog = getEmptyBagDialog();
    if (!dialog) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }

    window.clearTimeout(emptyBagCloseTimer);
    emptyBagReturnFocus = trigger || document.activeElement;
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cove-empty-bag-active');
    document.body.classList.add('overflow-hidden');

    window.requestAnimationFrame(() => {
      dialog.classList.add('cove-empty-bag--active');
      const panel = dialog.querySelector('.cove-empty-bag__panel');
      const closeButton = dialog.querySelector('.cove-empty-bag__close');

      window.setTimeout(() => {
        if (typeof trapFocus === 'function' && panel && closeButton) {
          trapFocus(panel, closeButton);
        } else {
          closeButton?.focus();
        }
      }, 60);
    });
  };

  const initEmptyBagDialog = () => {
    if (emptyBagHandlersBound || !getEmptyBagDialog()) return;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const closeControl = target?.closest('[data-cove-empty-bag-close]');

        if (closeControl && getEmptyBagDialog()?.contains(closeControl)) {
          event.preventDefault();
          closeEmptyBagDialog();
          return;
        }

        const cartLink = target?.closest('#cart-icon-bubble');
        if (!cartLink || cartLink.querySelector('.cart-count-bubble')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        openEmptyBagDialog(cartLink);
      },
      { capture: true }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.code === 'Escape' && getEmptyBagDialog()?.classList.contains('cove-empty-bag--active')) {
          event.preventDefault();
          closeEmptyBagDialog();
          return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const cartLink = target?.closest('#cart-icon-bubble');
        if (
          event.code.toUpperCase() !== 'SPACE' ||
          !cartLink ||
          cartLink.querySelector('.cart-count-bubble')
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        openEmptyBagDialog(cartLink);
      },
      { capture: true }
    );

    emptyBagHandlersBound = true;
  };

  const closeMenuFocus = (event) => {
    const headerDrawer = document.querySelector('.cove-fixed-header-shell header-drawer');
    const menuDetails = document.querySelector(
      '.cove-fixed-header-shell #Details-menu-drawer-container[open]'
    );
    const summary = menuDetails?.querySelector(':scope > summary');

    if (!menuDetails || !summary) return;

    if (headerDrawer && typeof headerDrawer.closeMenuDrawer === 'function') {
      headerDrawer.closeMenuDrawer(event, summary);
      summary.setAttribute('aria-expanded', 'false');
      return;
    }

    menuDetails.classList.remove('menu-opening');
    menuDetails.removeAttribute('open');
    summary.setAttribute('aria-expanded', 'false');
    document
      .querySelector('.cove-fixed-header-shell .section-header')
      ?.classList.remove('menu-open');
    document.body.classList.remove('overflow-hidden-desktop', 'overflow-hidden-tablet');
    document.documentElement.classList.remove('cove-menu-focus-active');
    document
      .querySelector('[data-cove-menu-focus-backdrop]')
      ?.classList.remove('cove-menu-focus-backdrop--active');
    summary.focus();
  };

  const getMenuFocusBackdrop = () => {
    let backdrop = document.querySelector('[data-cove-menu-focus-backdrop]');
    if (backdrop) return backdrop;

    backdrop = document.createElement('div');
    backdrop.className = 'cove-menu-focus-backdrop';
    backdrop.dataset.coveMenuFocusBackdrop = '';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.append(backdrop);
    return backdrop;
  };

  const initMenuFocusBackdrop = () => {
    const backdrop = getMenuFocusBackdrop();
    const headerSection = document.querySelector('.cove-fixed-header-shell .section-header');
    const menuDetails = document.querySelector(
      '.cove-fixed-header-shell #Details-menu-drawer-container'
    );

    if (!menuFocusPointerHandlerBound) {
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!document.documentElement.classList.contains('cove-menu-focus-active')) return;

          const target = event.target instanceof Element ? event.target : null;
          if (
            !target ||
            target.closest('.cove-fixed-header-shell .menu-drawer') ||
            target.closest('.cove-fixed-header-shell .header')
          ) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          closeMenuFocus(event);
        },
        { capture: true }
      );
      menuFocusPointerHandlerBound = true;
    }

    menuFocusObserver?.disconnect();

    const syncMenuFocus = () => {
      const isOpen = Boolean(
        headerSection?.classList.contains('menu-open') ||
        menuDetails?.classList.contains('menu-opening')
      );

      document.documentElement.classList.toggle('cove-menu-focus-active', isOpen);
      backdrop.classList.toggle('cove-menu-focus-backdrop--active', isOpen);
    };

    if (!headerSection || !menuDetails) {
      syncMenuFocus();
      return;
    }

    menuFocusObserver = new MutationObserver(syncMenuFocus);
    menuFocusObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ['class'],
    });
    menuFocusObserver.observe(menuDetails, {
      attributes: true,
      attributeFilter: ['class', 'open'],
    });
    syncMenuFocus();
  };

  const getCartNotificationBackdrop = () => {
    let backdrop = document.querySelector('[data-cove-cart-notification-backdrop]');
    if (backdrop) return backdrop;

    backdrop = document.createElement('div');
    backdrop.className = 'cove-cart-notification-backdrop';
    backdrop.dataset.coveCartNotificationBackdrop = '';
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.append(backdrop);
    return backdrop;
  };

  const initCartNotificationBackdrop = () => {
    const backdrop = getCartNotificationBackdrop();
    const notification = document.querySelector('#cart-notification');

    const syncCartNotificationBackdrop = () => {
      const isOpen = Boolean(notification?.classList.contains('active'));
      document.documentElement.classList.toggle('cove-cart-notification-active', isOpen);
      backdrop.classList.toggle('cove-cart-notification-backdrop--active', isOpen);
    };

    if (!cartNotificationBackdropHandlerBound) {
      backdrop.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.close === 'function') {
          cartNotification.close();
        }
      });
      cartNotificationBackdropHandlerBound = true;
    }

    cartNotificationObserver?.disconnect();

    if (!notification) {
      syncCartNotificationBackdrop();
      return;
    }

    cartNotificationObserver = new MutationObserver(syncCartNotificationBackdrop);
    cartNotificationObserver.observe(notification, {
      attributes: true,
      attributeFilter: ['class'],
    });
    syncCartNotificationBackdrop();
  };

  const prepareLoadedProduct = (item) => {
    item.classList.remove('scroll-trigger', 'animate--slide-in');
    item.classList.add('cove-product-loaded');
    item.removeAttribute('data-cascade');
    item.style.removeProperty('--animation-order');
    return item;
  };

  const loadAllCollectionProducts = async (button) => {
    if (button.dataset.loading === 'true') return;

    const grid = document.querySelector('#ProductGridContainer #product-grid');
    const status = button
      .closest('[data-cove-load-all-container]')
      ?.querySelector('[data-cove-load-all-status]');
    const label = button.querySelector('[data-cove-load-all-label]');
    const spinner = button.querySelector('.loading__spinner');
    const originalNextUrl = button.dataset.nextUrl;

    if (!grid || !originalNextUrl) return;

    button.dataset.loading = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Loading the collection';
    spinner?.classList.remove('hidden');

    let nextUrl = originalNextUrl;
    let loadedCount = Number(button.dataset.loadedCount || grid.children.length);
    const totalCount = Number(button.dataset.totalCount || loadedCount);

    try {
      while (nextUrl) {
        const requestUrl = new URL(nextUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', button.dataset.sectionId);

        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) {
          throw new Error(`Collection request failed with ${response.status}`);
        }

        const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const responseGrid = responseDocument.querySelector('#product-grid');
        const responseButton = responseDocument.querySelector('[data-cove-load-all]');

        if (!responseGrid) throw new Error('The next product grid was unavailable.');

        const products = Array.from(responseGrid.children).map(prepareLoadedProduct);
        grid.append(...products);
        loadedCount += products.length;
        nextUrl = responseButton?.dataset.nextUrl || '';

        if (status) {
          status.textContent = `Showing ${Math.min(loadedCount, totalCount)} of ${totalCount} products`;
        }
      }

      const container = button.closest('[data-cove-load-all-container]');
      if (status) status.textContent = `All ${totalCount} products are displayed`;
      button.remove();
      container?.classList.add('cove-load-all--complete');
      if (latestCartData) syncProductBagStates(latestCartData);
    } catch (error) {
      console.warn('[The Cove] The full collection could not be loaded.', error);
      button.dataset.nextUrl = nextUrl || originalNextUrl;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.dataset.loading = 'false';
      if (label) label.textContent = 'Try loading all again';
      spinner?.classList.add('hidden');
      if (status) status.textContent = 'The remaining products could not load. Please try again.';
    }
  };

  const initLoadAllProducts = () => {
    if (loadAllProductsHandlerBound) return;

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-cove-load-all]');
      if (!button) return;

      event.preventDefault();
      loadAllCollectionProducts(button);
    });

    loadAllProductsHandlerBound = true;
  };

  const getProgressHosts = () => {
    const hosts = [
      document.querySelector('#CartDrawer .drawer__footer'),
      document.querySelector('#main-cart-footer .cart__footer'),
    ];

    return [...new Set(hosts.filter(Boolean))];
  };

  const createProgress = (host) => {
    let progress = host.querySelector(':scope > .cove-shipping-progress');
    if (progress) return progress;

    progress = document.createElement('div');
    progress.className = 'cove-shipping-progress';
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');
    progress.innerHTML = `
      <p class="cove-shipping-progress__message"></p>
      <div class="cove-shipping-progress__track" aria-hidden="true">
        <span class="cove-shipping-progress__fill"></span>
      </div>
    `;
    host.prepend(progress);
    return progress;
  };

  const renderProgress = ({ totalCents = 0, itemCount = 0, currency = defaultCurrency } = {}) => {
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD_CENTS - totalCents, 0);
    const percent = Math.min((totalCents / FREE_SHIPPING_THRESHOLD_CENTS) * 100, 100);

    getProgressHosts().forEach((host) => {
      const progress = createProgress(host);
      const message = progress.querySelector('.cove-shipping-progress__message');
      const fill = progress.querySelector('.cove-shipping-progress__fill');

      fill.style.width = `${percent}%`;

      if (itemCount === 0) {
        message.textContent = `Complimentary U.S. shipping on orders ${formatMoney(
          FREE_SHIPPING_THRESHOLD_CENTS,
          currency
        )}+.`;
      } else if (remaining > 0) {
        message.textContent = `You are ${formatMoney(remaining, currency)} away from complimentary U.S. shipping.`;
      } else {
        message.textContent = 'Complimentary U.S. shipping unlocked.';
      }
    });
  };

  const normalizeStandardCart = (cart) => {
    if (!cart) return null;
    const amount = Number(cart.cost?.totalAmount?.amount);
    if (!Number.isFinite(amount)) return null;

    return {
      totalCents: Math.round(amount * 100),
      itemCount: Number(cart.totalQuantity || 0),
      currency: cart.cost?.totalAmount?.currencyCode || defaultCurrency,
    };
  };

  const getVariantQuantities = (cart) => {
    const quantities = new Map();

    (cart?.items || []).forEach((item) => {
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId) return;
      quantities.set(variantId, (quantities.get(variantId) || 0) + Number(item.quantity || 0));
    });

    return quantities;
  };

  const getProductFormElements = (productForm) => {
    const variantInput = productForm.querySelector('form input[name="id"]');
    const submitButton = productForm.querySelector('form [type="submit"]');
    const mainLabel = submitButton?.querySelector(
      ':scope > span:not(.sold-out-message):not(.loading__spinner)'
    );

    return { variantInput, submitButton, mainLabel };
  };

  const restoreProductBagState = (productForm) => {
    if (productForm.dataset.coveAlreadyInBag !== 'true') return;

    const { submitButton, mainLabel } = getProductFormElements(productForm);
    productForm.dataset.coveAlreadyInBag = 'false';

    if (!submitButton || !mainLabel) return;

    mainLabel.textContent = submitButton.dataset.coveOriginalLabel || window.variantStrings?.addToCart || 'Add to cart';
    submitButton.classList.remove('cove-already-in-bag');
    submitButton.removeAttribute('data-cove-already-in-bag');

    if (submitButton.dataset.coveOriginalAriaLabel) {
      submitButton.setAttribute('aria-label', submitButton.dataset.coveOriginalAriaLabel);
    } else {
      submitButton.removeAttribute('aria-label');
    }
  };

  const applyProductBagState = (productForm, quantity) => {
    const { submitButton, mainLabel } = getProductFormElements(productForm);
    if (!submitButton || !mainLabel || submitButton.hasAttribute('disabled')) return;

    if (!submitButton.dataset.coveOriginalLabel) {
      submitButton.dataset.coveOriginalLabel = mainLabel.textContent.trim();
      submitButton.dataset.coveOriginalAriaLabel = submitButton.getAttribute('aria-label') || '';
    }

    productForm.dataset.coveAlreadyInBag = 'true';
    submitButton.dataset.coveAlreadyInBag = 'true';
    submitButton.classList.add('cove-already-in-bag');
    submitButton.removeAttribute('aria-disabled');
    submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
    mainLabel.classList.remove('hidden');
    mainLabel.textContent = 'Already in your bag';
    submitButton.setAttribute(
      'aria-label',
      `${quantity} of this item is already in your bag. Open your bag.`
    );

    const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
    errorWrapper?.setAttribute('hidden', '');
  };

  const syncProductBagStates = (cart) => {
    const variantQuantities = getVariantQuantities(cart);

    document.querySelectorAll('product-form').forEach((productForm) => {
      const { variantInput } = getProductFormElements(productForm);
      const variantId = String(variantInput?.value || '');
      const quantity = variantQuantities.get(variantId) || 0;

      if (quantity > 0) applyProductBagState(productForm, quantity);
      else restoreProductBagState(productForm);
    });
  };

  const openCustomerBag = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    window.location.assign(window.routes?.cart_url || '/cart');
  };

  const initProductBagStates = () => {
    if (productBagStateHandlersBound) return;

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target instanceof HTMLFormElement ? event.target : null;
        const productForm = form?.closest('product-form[data-cove-already-in-bag="true"]');
        if (!productForm) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        openCustomerBag();
      },
      { capture: true }
    );

    document.addEventListener('change', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.matches('product-form input[name="id"]')) return;

      window.requestAnimationFrame(() => {
        if (latestCartData) syncProductBagStates(latestCartData);
      });
    });

    document.addEventListener('product-info:loaded', () => {
      window.requestAnimationFrame(() => {
        if (latestCartData) syncProductBagStates(latestCartData);
      });
    });

    productBagStateHandlersBound = true;
  };

  const fetchCart = async () => {
    if (requestInFlight) return requestInFlight;

    const root = window.Shopify?.routes?.root || '/';
    requestInFlight = fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        latestCartData = cart;
        renderProgress({
          totalCents: Number(cart.total_price || 0),
          itemCount: Number(cart.item_count || 0),
          currency: cart.currency || defaultCurrency,
        });
        syncProductBagStates(cart);
        return cart;
      })
      .catch((error) => {
        console.warn('[The Cove] The shipping progress could not update.', error);
      })
      .finally(() => {
        requestInFlight = null;
      });

    return requestInFlight;
  };

  const scheduleCartFetch = (delay = 100) => {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(fetchCart, delay);
  };

  const listenForStandardEvents = () => {
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (!event.promise || typeof event.promise.then !== 'function') {
        scheduleCartFetch();
        return;
      }

      event.promise
        .then(({ cart }) => {
          const normalized = normalizeStandardCart(cart);
          if (normalized) renderProgress(normalized);
          else scheduleCartFetch(0);
        })
        .catch(() => scheduleCartFetch());
    });

    document.addEventListener('shopify:cart:discount-update', (event) => {
      event.promise?.then(({ cart }) => {
        const normalized = normalizeStandardCart(cart);
        if (normalized) renderProgress(normalized);
        else scheduleCartFetch(0);
      }).catch(() => scheduleCartFetch());
    });
  };

  const listenForDawnPubSub = () => {
    try {
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        dawnUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleCartFetch());
      }
    } catch (error) {
      console.warn('[The Cove] Dawn cart subscription was unavailable.', error);
    }
  };

  const observeCartRegions = () => {
    const regions = [document.querySelector('cart-drawer'), document.querySelector('#main-cart-footer')].filter(Boolean);

    regions.forEach((region) => {
      const observer = new MutationObserver((mutations) => {
        const meaningfulChange = mutations.some((mutation) => {
          const target = mutation.target instanceof Element
            ? mutation.target
            : mutation.target.parentElement;
          return !target?.closest('.cove-shipping-progress');
        });

        if (meaningfulChange) scheduleCartFetch(140);
      });

      observer.observe(region, { childList: true, subtree: true });
    });
  };

  const init = () => {
    initFixedHeader();
    initPageNavigationScrollReset();
    resetPageScrollPosition(Boolean(window.__coveResetScrollRequested));
    initEmptyBagDialog();
    initMenuFocusBackdrop();
    initCartNotificationBackdrop();
    initLoadAllProducts();
    initProductBagStates();
    setHeaderState();
    listenForStandardEvents();
    listenForDawnPubSub();
    observeCartRegions();
    scheduleCartFetch(0);

    window.addEventListener('scroll', setHeaderState, { passive: true });
    document.addEventListener('cart:updated', () => scheduleCartFetch());
    document.addEventListener('shopify:section:load', () => {
      initFixedHeader();
      initMenuFocusBackdrop();
      initCartNotificationBackdrop();
      scheduleCartFetch();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', () => {
    resetPageScrollPosition(Boolean(window.__coveResetScrollRequested));
  });

  window.addEventListener('pagehide', () => {
    if (typeof dawnUnsubscribe === 'function') dawnUnsubscribe();
    menuFocusObserver?.disconnect();
    cartNotificationObserver?.disconnect();
    window.clearTimeout(emptyBagCloseTimer);
    scrollResetTimers.forEach((timer) => window.clearTimeout(timer));
    scrollResetTimers = [];
    document.documentElement.classList.remove('cove-menu-focus-active');
    document.documentElement.classList.remove('cove-cart-notification-active');
    document.documentElement.classList.remove('cove-empty-bag-active');
  });
})();
